/**
 * API statistiques établissement de santé.
 * Base URL : /api/etablissement/statistiques
 */

import { apiFetch } from "./client";

const BASE = "/etablissement/statistiques";

/** apiFetch renvoie l'enveloppe backend brute ({success, message, data}) telle quelle — jamais
 * déballée automatiquement. Sans ce déballage, `res.data` est l'enveloppe elle-même et toute
 * lecture de champ (ex. `res.data.consultations.total`) tombe silencieusement sur `undefined`. */
function extractPayload<T>(raw: unknown): T | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
    return (raw as { data?: T }).data;
  }
  return raw as T;
}

export type Period = "30j" | "60j" | "90j" | "6m" | "9m" | "12m" | "1a";

// ─── Types réponses ────────────────────────────────────────────────────────────

export interface ActiviteData {
  period: string;
  consultations: {
    total: number;
    surPeriode: number;
    parType: Record<string, number>;
    parStatut: Record<string, number>;
    evolutionMensuelle: Array<{ mois: string; total: number }>;
  };
  professionnelsActifs: number;
  patientsDistincts: number;
}

export interface EtatsSortieData {
  period: string;
  totalFermees: number;
  parEtat: Record<string, number>;
  taux: {
    guerison: number;
    sortiSansAvis: number;
    deces: number;
  };
  consultationsNonCloturees: number;
}

export interface RessourcesData {
  lits: {
    total: number;
    parStatut: Record<string, number>;
    tauxOccupation: number;
  };
  produits: {
    total: number;
    parStatut: Record<string, number>;
    alertes: {
      rupturesStock: number;
      expires: number;
    };
  };
  banqueSang: {
    total: number;
    parGroupeRhesus: Record<
      string,
      {
        disponible: number;
        reservee: number;
        utilisee: number;
        expiree: number;
        rejetee: number;
      }
    >;
  };
}

export interface RapportItem {
  _id: string;
  type: string;
  titre: string;
  statut: string;
  dateCreation: string;
  dateGeneration?: string;
}

export interface RapportsDocumentsData {
  rapports: {
    total: number;
    parType: Record<string, number>;
    parStatut: Record<string, number>;
    recents: RapportItem[];
  };
  notifications: {
    total: number;
    nonLues: number;
    parType: Record<string, number>;
  };
}

// ─── Fonctions fetch ───────────────────────────────────────────────────────────

export async function fetchActivite(period: Period) {
  const out = await apiFetch<unknown>(`${BASE}/activite?period=${period}`);
  if (!out.ok) return out;
  return { ok: true as const, data: extractPayload<ActiviteData>(out.data) as ActiviteData };
}

export async function fetchEtatsSortie(period: Period) {
  const out = await apiFetch<unknown>(`${BASE}/etats-sortie?period=${period}`);
  if (!out.ok) return out;
  return { ok: true as const, data: extractPayload<EtatsSortieData>(out.data) as EtatsSortieData };
}

export async function fetchRessources() {
  const out = await apiFetch<unknown>(`${BASE}/ressources`);
  if (!out.ok) return out;
  return { ok: true as const, data: extractPayload<RessourcesData>(out.data) as RessourcesData };
}

export async function fetchRapportsDocuments() {
  const out = await apiFetch<unknown>(`${BASE}/rapports-documents`);
  if (!out.ok) return out;
  return { ok: true as const, data: extractPayload<RapportsDocumentsData>(out.data) as RapportsDocumentsData };
}
